<template>
  <section class="page-section">
    <div class="toolbar">
      <h2 class="section-title">审核中心</h2>

      <div class="toolbar-actions">
        <el-input
          class="toolbar-field toolbar-field-keyword"
          v-model="filters.keyword"
          placeholder="搜索企业、标题、周次或摘要"
          clearable
          @keyup.enter="handleSearch"
        />
        <el-select
          class="toolbar-field toolbar-field-enterprise"
          v-model="filters.enterpriseId"
          placeholder="全部企业"
          clearable
          filterable
        >
          <el-option
            v-for="item in enterpriseOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <el-select
          v-model="filters.status"
          class="toolbar-field toolbar-field-status"
          placeholder="全部状态"
          clearable
        >
          <el-option
            v-for="item in REPORT_STATUS_OPTIONS"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-button @click="handleSearch">查询</el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>
    </div>

    <el-table :data="list" v-loading="loading">
      <el-table-column label="企业" min-width="220">
        <template #default="{ row }">
          <div class="enterprise-cell">
            <el-avatar :src="row.logo" :size="36">{{ row.enterprise_name.slice(0, 1) }}</el-avatar>
            <div class="enterprise-copy">
              <div class="enterprise-name">{{ row.enterprise_name }}</div>
              <div class="muted">
                {{ row.source_document ? '含 Word 原件 / ' : '' }}{{ row.attachment_count }} 个附件
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="report_week" label="周次" width="120" />
      <el-table-column prop="title" label="标题" min-width="260" />
      <el-table-column prop="status" label="状态" width="110">
        <template #default="{ row }">
          <el-tag :type="REPORT_STATUS_TYPE_MAP[row.status]">
            {{ REPORT_STATUS_TEXT_MAP[row.status] }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="submitted_at" label="提交时间" width="170" />
      <el-table-column prop="reviewed_at" label="审核时间" width="170" />
      <el-table-column prop="review_comment" label="审核意见" min-width="220" show-overflow-tooltip>
        <template #default="{ row }">
          {{ row.review_comment || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template #default="{ row }">
          <el-button link @click="openDetail(row.id)">查看</el-button>
          <el-button v-if="row.can_review" link type="primary" @click="openDetail(row.id, true)">
            审核
          </el-button>
          <el-button link type="danger" @click="handleDeleteReport(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next, sizes"
        :current-page="pager.page"
        :page-size="pager.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="pager.total"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
      />
    </div>

    <el-dialog v-model="detailVisible" title="周报详情" width="min(980px, calc(100vw - 32px))">
      <div v-loading="detailLoading" class="detail-wrap">
        <div v-if="detail" class="detail-panel">
          <div class="detail-head">
            <div class="detail-meta">
              <el-tag type="info">{{ detail.enterprise?.name || detail.enterprise_name }}</el-tag>
              <el-tag :type="REPORT_STATUS_TYPE_MAP[detail.status]">
                {{ REPORT_STATUS_TEXT_MAP[detail.status] }}
              </el-tag>
              <el-tag v-if="detail.source_document" type="success" effect="plain">Word 原件</el-tag>
              <span class="muted">周次：{{ detail.report_week }}</span>
              <span class="muted">提交：{{ detail.submitted_at || '-' }}</span>
              <span class="muted">审核：{{ detail.reviewed_at || '-' }}</span>
              <el-button link type="danger" :loading="deletingReport" @click="handleDeleteReport(detail)">删除周报</el-button>
            </div>
            <h3>{{ detail.title }}</h3>
          </div>

          <p v-if="detail.review_comment" class="detail-note">审核意见：{{ detail.review_comment }}</p>

          <img v-if="detail.cover_image" class="detail-cover" :src="detail.cover_image" alt="cover" />
          <p v-if="detail.summary" class="detail-summary">{{ detail.summary }}</p>

          <div v-if="detail.source_document" class="attachment-box">
            <div class="attachment-title">原始 Word</div>
            <div class="attachment-actions">
              <el-button link type="danger" :loading="removingSourceDocument" @click="handleDeleteSourceDocument">
                删除原件
              </el-button>
            </div>
            <div class="attachment-list">
              <a
                :href="detail.source_document.file_url"
                target="_blank"
                rel="noreferrer"
                class="attachment-item"
              >
                <el-tag type="success">Word</el-tag>
                <span>{{ detail.source_document.file_name }}</span>
                <span class="muted">{{ formatFileSize(detail.source_document.file_size) }}</span>
              </a>
            </div>
          </div>

          <div v-if="detail.attachments?.length" class="attachment-box">
            <div class="attachment-title">关联附件</div>
            <div class="attachment-list">
              <a
                v-for="item in detail.attachments"
                :key="item.id"
                :href="item.file_url"
                target="_blank"
                rel="noreferrer"
                class="attachment-item"
              >
                <el-tag :type="item.file_type === 'video' ? 'danger' : 'success'">
                  {{ item.file_type === 'video' ? '视频' : '图片' }}
                </el-tag>
                <span>{{ item.file_name }}</span>
              </a>
            </div>
          </div>

          <div class="detail-body" v-html="sanitizedDetailContent"></div>

          <div v-if="detail.can_review" class="review-box">
            <div class="review-head">
              <h4>审核处理</h4>
            </div>

            <el-input
              v-model="reviewForm.review_comment"
              type="textarea"
              :rows="4"
              maxlength="500"
              show-word-limit
            />

            <div class="review-actions">
              <el-button type="success" :loading="reviewing" @click="handleReview(2)">审核通过</el-button>
              <el-button type="danger" plain :loading="reviewing" @click="handleReview(3)">
                驳回修改
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useRoute, useRouter } from 'vue-router';

import { getAdminEnterpriseList } from '../../api/enterprise';
import {
  deleteAdminReport,
  deleteAdminReportSourceDocument,
  getAdminReportDetail,
  getAdminReportList,
  reviewAdminReport
} from '../../api/report';
import {
  REPORT_STATUS_OPTIONS,
  REPORT_STATUS_TEXT_MAP,
  REPORT_STATUS_TYPE_MAP
} from '../../constants/report';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const detailLoading = ref(false);
const detailVisible = ref(false);
const reviewing = ref(false);
const deletingReport = ref(false);
const removingSourceDocument = ref(false);
const list = ref([]);
const detail = ref(null);
const enterpriseOptions = ref([]);
const sanitizedDetailContent = computed(() => sanitizeHtml(detail.value?.content || ''));

const filters = reactive({
  keyword: '',
  enterpriseId: '',
  status: ''
});

const pager = reactive({
  page: 1,
  pageSize: 10,
  total: 0
});

const reviewForm = reactive({
  review_comment: ''
});

const formatFileSize = (size) => {
  const value = Number(size) || 0;
  if (!value) return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const loadEnterprises = async () => {
  try {
    const result = await getAdminEnterpriseList();
    enterpriseOptions.value = result.data || [];
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '获取企业列表失败');
  }
};

const loadList = async () => {
  loading.value = true;
  try {
    const result = await getAdminReportList({
      page: pager.page,
      pageSize: pager.pageSize,
      status: filters.status,
      keyword: filters.keyword,
      enterpriseId: filters.enterpriseId
    });
    list.value = result.data.list || [];
    pager.total = result.data.total || 0;
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '获取审核列表失败');
  } finally {
    loading.value = false;
  }
};

const openDetail = async (id, focusReview = false) => {
  detailVisible.value = true;
  detailLoading.value = true;
  reviewForm.review_comment = '';

  try {
    const result = await getAdminReportDetail(id);
    detail.value = result.data;
    if (!focusReview && detail.value.review_comment) {
      reviewForm.review_comment = detail.value.review_comment;
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '获取周报详情失败');
  } finally {
    detailLoading.value = false;
  }
};

const handleSearch = () => {
  pager.page = 1;
  loadList();
};

const handleReset = () => {
  filters.keyword = '';
  filters.enterpriseId = '';
  filters.status = '';
  pager.page = 1;
  pager.pageSize = 10;
  loadList();
};

const handlePageChange = (page) => {
  pager.page = page;
  loadList();
};

const handleSizeChange = (pageSize) => {
  pager.page = 1;
  pager.pageSize = pageSize;
  loadList();
};

const handleDeleteReport = async (row) => {
  if (!row?.id) return;

  try {
    await ElMessageBox.confirm(
      `\u786e\u5b9a\u5220\u9664\u300a${row.title}\u300b\u5417\uff1f`,
      '\u5220\u9664\u786e\u8ba4',
      {
      type: 'warning'
      }
    );
  } catch (error) {
    return;
  }

  deletingReport.value = true;

  try {
    await deleteAdminReport(row.id);
    ElMessage.success('\u5220\u9664\u6210\u529f');

    if (detail.value?.id === row.id) {
      detailVisible.value = false;
      detail.value = null;
    }

    if (list.value.length === 1 && pager.page > 1) {
      pager.page -= 1;
    }

    await loadList();
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '\u5220\u9664\u5931\u8d25');
  } finally {
    deletingReport.value = false;
  }
};

const handleDeleteSourceDocument = async () => {
  if (!detail.value?.source_document) return;

  try {
    await ElMessageBox.confirm('删除后将无法继续下载这份 Word 原件，确认删除吗？', '删除确认', {
      type: 'warning'
    });
  } catch (error) {
    return;
  }

  removingSourceDocument.value = true;

  try {
    const result = await deleteAdminReportSourceDocument(detail.value.id);
    detail.value = result.data;
    ElMessage.success(result.message || 'Word 原件已删除');
    await loadList();
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '删除原件失败');
  } finally {
    removingSourceDocument.value = false;
  }
};

const handleReview = async (status) => {
  if (!detail.value) return;

  if (status === 3 && !reviewForm.review_comment.trim()) {
    ElMessage.warning('驳回时请填写审核意见');
    return;
  }

  reviewing.value = true;
  try {
    const result = await reviewAdminReport(detail.value.id, {
      status,
      review_comment: reviewForm.review_comment
    });
    ElMessage.success(result.message || '审核完成');
    await loadList();
    await openDetail(detail.value.id);
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '审核失败');
  } finally {
    reviewing.value = false;
  }
};

watch(
  () => route.query.id,
  async (id) => {
    if (!id) return;
    await openDetail(id, true);

    const query = { ...route.query };
    delete query.id;
    router.replace({ query });
  },
  { immediate: true }
);

onMounted(async () => {
  await Promise.all([loadEnterprises(), loadList()]);
});
</script>

<style scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.toolbar-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1 1 520px;
}

.toolbar-field {
  width: 100%;
}

.toolbar-field-keyword {
  flex: 1 1 260px;
  max-width: 320px;
}

.toolbar-field-enterprise {
  flex: 1 1 220px;
  max-width: 260px;
}

.toolbar-field-status {
  flex: 0 0 140px;
  max-width: 160px;
}

.enterprise-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.enterprise-copy {
  min-width: 0;
}

.enterprise-name {
  font-weight: 600;
  word-break: break-word;
}

.pager {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
}

.detail-wrap {
  min-height: 180px;
}

.detail-panel {
  display: grid;
  gap: 18px;
}

.detail-head h3 {
  margin: 10px 0 0;
  font-size: 28px;
  word-break: break-word;
}

.detail-meta {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.detail-note {
  margin: -4px 0 0;
  color: #4b5563;
  line-height: 1.7;
  word-break: break-word;
}

.detail-cover {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  border-radius: 12px;
}

.detail-summary {
  margin: 0;
  padding: 14px 16px;
  background: #f9fafb;
  border-radius: 8px;
  color: #4b5563;
  line-height: 1.8;
  word-break: break-word;
}

.attachment-box {
  display: grid;
  gap: 12px;
}

.attachment-title {
  font-weight: 600;
}

.attachment-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: -4px;
}

.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.attachment-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  max-width: 100%;
  word-break: break-word;
}

.detail-body {
  line-height: 1.9;
  word-break: break-word;
  overflow: hidden;
}

.review-box {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
  background: #f8fafc;
}

.review-head h4 {
  margin: 0;
  font-size: 18px;
}

.review-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .toolbar-actions {
    flex-basis: 100%;
  }

  .toolbar-field-keyword,
  .toolbar-field-enterprise,
  .toolbar-field-status {
    max-width: none;
  }
}
</style>
